import React, { useState, useEffect } from "react";
import { loginUser, verifyToken } from "shared/api/client/orvalHelpers";
import { useNavigate, Navigate, useLocation } from "react-router-dom";
import { getCookie } from "shared/auth/utils/cookieUtils";
import { getAuthCookieAttributes } from "shared/auth/utils/cookieUtils";
import { getApiOrigin } from "shared/api/client/apiOrigin";
import MobileForgotPassword from "../components/MobileForgotPassword";
import MobileResetPassword from "../components/MobileResetPassword";
import { useTokenProcessing } from "shared/auth/hooks/useTokenProcessing";

// Styles
import "../../../styles/legacy/MobileLogin.css";

const MobileLoginPage = () => {
  useTokenProcessing();
  const navigate = useNavigate();
  const location = useLocation();

  const isCreatorLogin = location.hash === "#k9x2m7p4q8r3v6n1w5z0";
  const userType = isCreatorLogin ? "Creator" : "Viewer";

  const GOOGLE_OAUTH_URL_LI = `${getApiOrigin()}/api/User/google-login/${userType}`;
  const FACEBOOK_OAUTH_URL_LI = `${getApiOrigin()}/api/User/facebook-login/${userType}`;

  const [isResetPasswordOpen, setIsResetPasswordOpen] = useState(false);

  const params = new URLSearchParams(window.location.search);
  const resetToken = params.get("resetToken");

  useEffect(() => {
    if (resetToken) {
      setIsResetPasswordOpen(true);
    }
  }, [resetToken]);

  const [isLoading, setIsLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isVerified, setIsVerified] = useState<boolean | null>(null);

  const [errorMessage, setErrorMessage] = useState("");
  const [messageBanner, setMessageBanner] = useState("");

  const [isForgotPasswordOpen, setIsForgotPasswordOpen] = useState(false);

  const openForgotPasswordModal = () => {
    setIsForgotPasswordOpen(true);
  };

  const closeForgotPasswordModal = () => {
    setIsForgotPasswordOpen(false);
  };

  useEffect(() => {
    const token = getCookie("userToken");
    if (!token) {
      setIsVerified(false);
      return;
    }
    verifyToken(token).then((result: boolean) => setIsVerified(result));
  }, []);

  if (isVerified === null) {
    return <>Loading...</>;
  }

  if (isVerified) {
    return <Navigate to="/pair_headset" replace />;
  }

  const handleLogin = async (event: React.MouseEvent<HTMLButtonElement>) => {
    setIsLoading(true);
    try {
      const data = await loginUser({ email: email, password: password });
      const userToken = data.token;
      const userId = data.id;
      const userIsVerified = data.user_is_verified;
      const userRole = data.user_role;
      const authCookieAttributes = getAuthCookieAttributes();
      document.cookie = `userToken=${userToken}; ${authCookieAttributes}`;
      document.cookie = `userId=${userId}; ${authCookieAttributes}`;
      document.cookie = `userIsVerified=${userIsVerified}; ${authCookieAttributes}`;
      document.cookie = `userRole=${userRole}; ${authCookieAttributes}`;
      document.cookie = `userEmail=${encodeURIComponent(data.email || email)}; ${authCookieAttributes}`;

      navigate("/pair_headset");
    } catch (err: any) {
      console.error("Error occurred:", err);
      const errResponse = err.message;
      let message = "An unexpected error occured";
      if (errResponse.includes("Invalid Email or password.")) {
        message = "Invalid email or password. Please try again.";
      } else if (
        errResponse.includes(
          "Google account connected. Please login with Google."
        )
      ) {
        message =
          "This email is already associated with a Google account. Try logging in with Google";
      }

      setErrorMessage(message);
      setTimeout(() => {
        setErrorMessage("");
      }, 3000);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="mobile-login">
      {errorMessage && (
        <div className="error-banner-mobile">
          <img
            src="/images/icons/triangle-warning.svg"
            alt="warning"
            className="warning-mobile"
          />
          <div className="error-banner-text-mobile">{errorMessage}</div>
        </div>
      )}
      {messageBanner && (
        <div className="message-banner-mobile">
          <div className="error-banner-text-mobile">{messageBanner}</div>
        </div>
      )}
      <img className="logo" src="/images/poetics-stream-lockup.svg" alt="Poetics Stream logo" />
      <div className="top-message-mobile">
        <div className="title-login-mobile">Welcome back</div>
        <div className="subtitle-login-mobile">
          Log in to manage your profile and pair your <br />
          devices
        </div>
      </div>
      <div className="mid-container-login-mobile">
        <div className="mid-container-text-mobile">Log in with</div>
        <div className="buttons-login-mobile">
          <button
            className="sso-button-mobile"
            onClick={() => (window.location.href = GOOGLE_OAUTH_URL_LI)}
          >
            <div className="sso-button-logo-text-mobile">
              <img
                src="/images/mobile/SSO_buttons/google.svg"
                alt="Google"
                className="sso-logo-mobile"
              />
              Google
            </div>
          </button>
          <button
            className="sso-button-mobile"
            onClick={() => (window.location.href = FACEBOOK_OAUTH_URL_LI)}
          >
            <div className="sso-button-logo-text-mobile">
              <svg
                viewBox="0 0 320 512"
                className="sso-logo-mobile"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  fill="currentColor"
                  d="M279.14 288l14.22-92.66h-88.91V117.13c0-25.35 12.42-50.06 52.24-50.06h40.42V6.26S260.43 0 225.36 0c-73.22 0-121.08 44.38-121.08 124.72v70.62H22.89V288h81.39v224h100.17V288z"
                />
              </svg>
              Facebook
            </div>
          </button>
        </div>
        <div className="mid-container-text-mobile">or</div>
        {isLoading ? (
          <div className="spinner-container-mobile">
            <span className="spinner-mobile" />
          </div>
        ) : (
          <div className="form-container-mobile">
            <input
              type="email"
              placeholder="Email"
              className="input-field-login-mobile"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
            <input
              type="password"
              placeholder="Password"
              className="input-field-login-mobile"
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />

            <button
              className="login-button"
              onClick={handleLogin}
              disabled={isLoading}
            >
              Log in
            </button>
            <div
              className="forgot-password-link"
              onClick={openForgotPasswordModal}
            >
              Forgot password?
            </div>
            <p className="create-account-mobile">
              Want to create an account? <a href="/mobile/signup">Sign up</a>
            </p>
          </div>
        )}
        {isForgotPasswordOpen && (
          <MobileForgotPassword
            onClose={closeForgotPasswordModal}
            setErrorMessage={setErrorMessage}
            setMessageBanner={setMessageBanner}
          />
        )}
        {isResetPasswordOpen && resetToken && (
          <MobileResetPassword
            onClose={() => {
              setIsResetPasswordOpen(false);
              navigate("/mobile/login", { replace: true });
            }}
            setErrorMessage={setErrorMessage}
            setMessageBanner={setMessageBanner}
            resetToken={resetToken}
          />
        )}
      </div>
    </div>
  );
};

export default MobileLoginPage;
